import { useState } from "react";
import { createOrder } from "@nusrif/services";
import { createPayment, verifyPayment } from "@nusrif/services";
import { trackEvent } from "@nusrif/services";
import { useNavigate } from "react-router";

export const usePaymentHandler = (cashfree: any, cartId?: string) => {
    const navigate = useNavigate();
    const [isProcessing, setIsProcessing] = useState(false);
    const [isVerifying, setIsVerifying] = useState(false);
    const [paymentStarted, setPaymentStarted] = useState(false);

    const handlePayment = async () => {
        if (!cartId) return;

        setIsProcessing(true);

        try {
            const orderRes = await createOrder(cartId);
            if (!orderRes.success) {
                // Track order creation failure
                trackEvent({
                    event: "payment_failed",
                    properties: {
                        cart_id: cartId,
                        failure_stage: "order_creation",
                        error_message: "Order creation failed",
                        order_id: null
                    }
                });
                throw new Error("Order creation failed");
            }

            const paymentRes = await createPayment(orderRes.data.id);
            if (!paymentRes.success) {
                // Track payment creation failure
                trackEvent({
                    event: "payment_failed",
                    properties: {
                        cart_id: cartId,
                        failure_stage: "payment_creation",
                        error_message: "Payment creation failed",
                        order_id: orderRes.data.id
                    }
                });
                throw new Error("Payment creation failed");
            }

            setPaymentStarted(true);

            // Get current window width and height
            const width = window.innerWidth;
            const height = window.innerHeight;

            const result = await cashfree.checkout({
                paymentSessionId: paymentRes.data.paymentSessionId,
                redirectTarget: document.getElementById("cf_checkout"),
                appearance: { width: `${width}px`, height: `${height}px` },
            });

            if (result.error) {
                console.log("There is some payment error, Check for Payment Status");
                console.log(result.error);

                // Track payment error
                trackEvent({
                    event: "payment_failed",
                    properties: {
                        cart_id: cartId,
                        failure_stage: "payment_processing",
                        error_message: result.error.message || "Payment processing error",
                        order_id: orderRes.data.id,
                        payment_session_id: paymentRes.data.paymentSessionId
                    }
                });

                const urlToRedirect = `/cart`
                return navigate(urlToRedirect, { replace: true });
            }

            if (result.redirect) {
                console.log("Payment will be redirected");

                // Track payment redirect
                trackEvent({
                    event: "payment_redirected",
                    properties: {
                        cart_id: cartId,
                        order_id: orderRes.data.id,
                        payment_session_id: paymentRes.data.paymentSessionId
                    }
                });
            }

            if (result.paymentDetails) {
                setIsVerifying(true);
                console.log("Payment has been completed, Check for Payment Status");
                console.log(result.paymentDetails.paymentMessage);

                // Verify payment status
                const verifyRes = await verifyPayment(orderRes.data.id);

                if (!verifyRes.success) {
                    // Track payment verification failure
                    trackEvent({
                        event: "payment_failed",
                        properties: {
                            cart_id: cartId,
                            failure_stage: "payment_verification",
                            error_message: "Payment verification failed",
                            order_id: orderRes.data.id,
                            payment_session_id: paymentRes.data.paymentSessionId
                        }
                    });
                    throw new Error("Payment verification failed");
                }

                // Track successful payment
                trackEvent({
                    event: "payment_successful",
                    properties: {
                        cart_id: cartId,
                        order_id: orderRes.data.id,
                        payment_session_id: paymentRes.data.paymentSessionId,
                        payment_message: result.paymentDetails.paymentMessage,
                        verified_order_id: verifyRes.data.orderId
                    }
                });

                const urlToRedirect = `/order-success?orderId=${verifyRes.data.orderId}`

                return navigate(urlToRedirect, { replace: true });
            }
        } catch (err) {
            setIsVerifying(false);
            console.error("Payment error:", err);

            // Track general payment failure
            trackEvent({
                event: "payment_failed",
                properties: {
                    cart_id: cartId,
                    failure_stage: "general_error",
                    error_message: err instanceof Error ? err.message : "Unknown payment error",
                    order_id: null
                }
            });

            const urlToRedirect = `/payment-failure`

            return navigate(urlToRedirect, { replace: true });
        } finally {
            setIsProcessing(false);
        }
    };

    return { handlePayment, isProcessing, paymentStarted, isVerifying };
};
